import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import React, { useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import AuthContext from '../../../contexes/AuthContext';
import { appendEmbedParam, getEmbedHomePath } from '../../../helpers/embedMode';
import { useCompanyAgents } from '../../../hooks/queries/useCompanyAgents';
import { useCompanyChatAgents } from '../../../hooks/queries/useCompanyChatAgents';
import { hasPartnerSession } from '../../../services/partnerSession';
import { canEditAgentResource } from '../../../utils/resourceAccess';
import Loading from '../Loading/Loading';

interface AgentOwnerOrAdminRouteProps {
  element: React.ReactElement;
  agentType: 'call' | 'chat';
  mode?: 'view' | 'manage';
}

const AgentOwnerOrAdminRoute: React.FC<AgentOwnerOrAdminRouteProps> = ({
  element,
  agentType,
  mode = 'view',
}) => {
  const { id } = useParams();
  const { accounts, inProgress } = useMsal();
  const { currentUser, isLoading: userLoading } = useContext(AuthContext);
  const { data: callAgents, isLoading: callAgentsLoading } =
    useCompanyAgents(!!currentUser);
  const { data: chatAgents, isLoading: chatAgentsLoading } =
    useCompanyChatAgents(!!currentUser);

  if (
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.AcquireToken
  ) {
    return <Loading />;
  }

  // A partner iframe (MixVoIP) session has no MSAL account.
  if (accounts.length === 0 && !hasPartnerSession()) {
    return <Navigate to={getEmbedHomePath()} />;
  }

  if (userLoading || !currentUser) {
    return <Loading />;
  }

  if (currentUser.permissions?.super_admin) {
    return element;
  }

  const agentsLoading =
    agentType === 'chat' ? chatAgentsLoading : callAgentsLoading;

  if (agentsLoading) {
    return <Loading />;
  }

  if (!id) {
    return <Navigate to={appendEmbedParam('/access-denied')} />;
  }

  if (agentType === 'chat') {
    const agent = chatAgents.find(item => item.id === id);
    if (!agent) {
      return <Navigate to={appendEmbedParam('/access-denied')} />;
    }
    if (mode === 'manage' && !canEditAgentResource(currentUser, agent)) {
      return <Navigate to={appendEmbedParam('/access-denied')} />;
    }
    return element;
  }

  const agent = callAgents.find(item => item.id === id);
  if (!agent) {
    return <Navigate to={appendEmbedParam('/access-denied')} />;
  }
  if (mode === 'manage' && !canEditAgentResource(currentUser, agent)) {
    return <Navigate to={appendEmbedParam('/access-denied')} />;
  }

  return element;
};

export default AgentOwnerOrAdminRoute;
